/**
 * AI CONTEXT
 * Purpose: interactive timeline for session window selection and session stripe navigation.
 * Features: dynamic scale, drag/resize selection, hour markers, current-time marker, auto-select sessions.
 * Depends on: AdminViewContext session/date state.
 * Used by: SuperView/AdminView left panel.
 * Refactor: split drag/resize/auto-scroll behavior into dedicated hooks.
 */
import { Spin, Tooltip } from "antd"
import dayjs, { Dayjs } from "dayjs"
import { first, last, orderBy } from "lodash"
import { motion } from "motion/react"
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import Flex from "#blocks/Flex"
import { useAdminViewContext } from "#contexts/AdminViewContextProvider"
import { Hexcode, useThemeContext } from "#contexts/ThemeContextProvider"
import { TimelineSession } from "#customTypes/composedTypes/classSessionTypes"
import { invariant } from "#customTypes/types"
import { formatTimeLocale, getNow, isToday } from "#utils/dateHelpers"

import CurrentTimeMarker from "./CurrentTimeMarker"
import DraggableSelectionArea from "./DraggableSelectionArea"
import SessionStripe from "./SessionStripe"

const START_TIME = 8
const END_TIME = 18
const MIN_TIMELINE_HOURS = 6 // Minimum timeline span to prevent overly tall layouts

const PADDING_LEFT = 30

const TimeLabel = ({
  time,
  marker,
}: {
  time: string
  marker: { time: string; position: number }
}) => (
  <div
    style={{
      position: "absolute",
      top: marker.position,
      left: 0, // Position to the left of the timeline
    }}
  >
    <div
      style={{
        fontSize: 10,
        color: "#666",
        width: 100,
        textAlign: "left",
        paddingRight: 8,
        borderTop: "1px solid #e0e0e0",
        marginBottom: 14,
      }}
    >
      {time}
    </div>
  </div>
)

const ClassScheduleTimeline = () => {
  const [isSelectionFocusedOnCurrentTime, setIsSelectionFocusedOnCurrentTime] =
    useState(false)
  const { allSessions, setSelectedSessionIds, isReady, selectedDate } =
    useAdminViewContext()
  const theme = useThemeContext()

  const timelineRef = useRef<HTMLDivElement>(null)
  const [selectedTimeStart, setSelectedTimeStart] = useState<Dayjs>(getNow())
  const [selectionDurationHours, setSelectionDurationHours] = useState(3)
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState<"top" | "bottom" | null>(null)
  const [dragStartY, setDragStartY] = useState(0)
  const [dragStartTime, setDragStartTime] = useState<Dayjs | null>(null)
  const [dragStartDuration, setDragStartDuration] = useState(0)
  const [shouldScrollAfterSessionClick, setShouldScrollAfterSessionClick] =
    useState(false)

  // State for showing selection info tooltip
  const [showSelectionTooltip, setShowSelectionTooltip] = useState(false)

  // State for auto-scrolling during drag
  const [autoScrollInterval, setAutoScrollInterval] = useState<ReturnType<
    typeof setInterval
  > | null>(null)

  // State to track scroll position during drag
  const [dragStartScrollTop, setDragStartScrollTop] = useState(0)

  // State to track timeouts for cleanup
  const [preventScrollTimeout, setPreventScrollTimeout] = useState<ReturnType<
    typeof setTimeout
  > | null>(null)

  // RAF refs for debouncing selection updates
  const selectionUpdateRafRef = useRef<number | null>(null)
  const sessionSelectionRafRef = useRef<number | null>(null)

  // State for container height to handle window resizing
  const [containerHeight, setContainerHeight] = useState(
    () => window.innerHeight - 130
  )

  // Handle window resize to update container height
  useLayoutEffect(() => {
    const handleResize = () => {
      setContainerHeight(window.innerHeight - 130)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Convert AdminViewClassSession to timeline format
  const timelineSessions: TimelineSession[] = useMemo(() => {
    if (!allSessions?.length) {
      return []
    }
    return orderBy(allSessions, "room").map((session) => ({
      id: session.id,
      name: session.name,
      room: session.room,
      color: session.color,
      eventStart: session.startTime,
      eventEnd: session.endTime,
      session: session.session,
    }))
  }, [allSessions])

  // Calculate selection window end time based on duration
  const selectedTimeEnd = useMemo(() => {
    return selectedTimeStart.add(selectionDurationHours, "hours")
  }, [selectedTimeStart, selectionDurationHours])

  // Get unique rooms and their colors
  const roomColumns = useMemo(() => {
    const rooms = new Map<string, Hexcode>()
    timelineSessions.forEach((session) => {
      rooms.set(session.room, session.color)
    })
    return Array.from(rooms.entries()).map(([room, color]) => ({ room, color }))
  }, [timelineSessions])

  // Calculate timeline bounds and dynamic height scaling
  const { startTime, endTime, totalMinutes, heightPerMinute } = useMemo(() => {
    if (timelineSessions.length === 0) {
      // Default bounds if no sessions
      const start = getNow().startOf("day").hour(START_TIME)
      const end = getNow().startOf("day").hour(END_TIME)
      const minutes = end.diff(start, "minutes")
      return {
        startTime: start,
        endTime: end,
        totalMinutes: minutes,
        heightPerMinute: containerHeight / minutes,
      }
    }

    const sortedTimes = timelineSessions
      .flatMap((s) => [s.eventStart, s.eventEnd])
      .sort((a, b) => a.diff(b, "minutes"))

    const firstTime = first(sortedTimes)
    const lastTime = last(sortedTimes)

    invariant(
      firstTime !== undefined,
      `First time should exist when times array has length ${sortedTimes.length}`
    )
    invariant(
      lastTime !== undefined,
      `Last time should exist when times array has length ${sortedTimes.length}`
    )

    const earliestSessionTime = dayjs(firstTime)
    const latestSessionTime = dayjs(lastTime)

    // Calculate how much time we need before first session to position it at 20%
    const timeBeforeFirstSession = 60 // 1 hour before first session

    // Calculate start time by going back from earliest session
    const calculatedStart = earliestSessionTime
      .subtract(timeBeforeFirstSession, "minutes")
      .startOf("hour")

    // Add buffer after last session
    const timeAfterLastSession = 60 // 1 hour after last session
    const calculatedEnd = latestSessionTime
      .add(timeAfterLastSession, "minutes")
      .startOf("hour")

    const totalTimelineMinutes = calculatedEnd.diff(calculatedStart, "minutes")
    const minTimelineMinutes = MIN_TIMELINE_HOURS * 60

    // Ensure minimum timeline span to prevent overly tall layouts
    let finalStart = calculatedStart
    let finalEnd = calculatedEnd
    let finalTotalMinutes = totalTimelineMinutes

    if (totalTimelineMinutes < minTimelineMinutes) {
      // Timeline is too short, expand it to minimum span
      const additionalMinutes = minTimelineMinutes - totalTimelineMinutes
      const additionalStart = Math.floor(additionalMinutes / 2)
      const additionalEnd = additionalMinutes - additionalStart

      finalStart = calculatedStart
        .subtract(additionalStart, "minutes")
        .startOf("hour")
      finalEnd = calculatedEnd.add(additionalEnd, "minutes").startOf("hour")
      finalTotalMinutes = finalEnd.diff(finalStart, "minutes")
    }

    // Calculate dynamic height per minute to fit screen perfectly
    const dynamicHeightPerMinute = containerHeight / finalTotalMinutes

    return {
      startTime: finalStart,
      endTime: finalEnd,
      totalMinutes: finalTotalMinutes,
      heightPerMinute: dynamicHeightPerMinute,
    }
  }, [timelineSessions, getNow, containerHeight])

  // Track previous selected date to detect date changes
  const prevSelectedDateRef = useRef<Dayjs | null>(null)

  // Reset scroll position when selected date changes
  useEffect(() => {
    const prevDate = prevSelectedDateRef.current
    const dateChanged = !prevDate || !prevDate.isSame(selectedDate, "day")

    if (
      dateChanged &&
      timelineRef.current &&
      timelineSessions.length > 0 &&
      !isDragging
    ) {
      // Only update ref after we've confirmed we have data and can perform the reset
      prevSelectedDateRef.current = selectedDate

      // Reset selection focus flag so auto-positioning can run again
      setIsSelectionFocusedOnCurrentTime(false)

      // Reset to a reasonable scroll position (20% from top)
      const timeoutId = setTimeout(() => {
        if (timelineRef.current) {
          const viewportHeight = timelineRef.current.clientHeight
          const targetPosition = viewportHeight * 0.2
          timelineRef.current.scrollTo({
            top: targetPosition,
            behavior: "smooth",
          })
        }
      }, 100) // Small delay to ensure DOM has updated

      return () => clearTimeout(timeoutId)
    }
  }, [selectedDate, timelineSessions.length, isDragging])

  // Calculate current time position (null if not viewing today)
  const currentTimePosition = useMemo(() => {
    const now = getNow()

    // Only show marker if viewing today's date
    if (!isToday(selectedDate, now)) {
      return null
    }

    // Create today's date with the timeline start/end times for comparison
    const todayStart = now.hour(startTime.hour()).minute(startTime.minute())

    const minutesFromStart = now.diff(todayStart, "minutes")
    const position = minutesFromStart * heightPerMinute

    // Clamp the position to the timeline bounds
    const OVERFLOW_PADDING = 10

    return Math.min(
      Math.max(position, -OVERFLOW_PADDING),
      totalMinutes * heightPerMinute + OVERFLOW_PADDING
    )
  }, [startTime, heightPerMinute, totalMinutes, selectedDate])

  // Calculate selection box position
  const selectionBoxPosition = useMemo(() => {
    const startMinutes = selectedTimeStart.diff(startTime, "minutes")
    const height = selectionDurationHours * 60 * heightPerMinute
    return {
      top: startMinutes * heightPerMinute,
      height,
    }
  }, [selectedTimeStart, startTime, selectionDurationHours, heightPerMinute])

  // Generate hour markers - only when ready to prevent duplicate keys during loading
  const hourMarkers = useMemo(() => {
    if (!isReady) {
      return []
    }

    const markers = []
    let current = startTime

    let bailCounter = 0

    while (current.isBefore(endTime) || current.isSame(endTime)) {
      // This is a safety check to prevent infinite loops
      bailCounter++
      if (bailCounter > 100) {
        console.log("Bailed out of hour markers")
        break
      }

      const minutesFromStart = current.diff(startTime, "minutes")
      markers.push({
        time: formatTimeLocale(current),
        position: minutesFromStart * heightPerMinute,
      })
      current = current.add(1, "hour")
    }
    return markers
  }, [startTime, endTime, heightPerMinute, isReady])

  // Position sessions within their room columns
  const positionedSessions = useMemo(() => {
    return timelineSessions.map((session) => {
      const columnIndex = roomColumns.findIndex(
        (col) => col.room === session.room
      )
      const startMinutes = session.eventStart.diff(startTime, "minutes")
      const duration = session.eventEnd.diff(session.eventStart, "minutes")

      // Check if session overlaps with selected time window
      const sessionStart = session.eventStart
      const sessionEnd = session.eventEnd
      const isInSelectedWindow =
        selectedTimeStart < sessionEnd && sessionStart < selectedTimeEnd

      return {
        ...session,
        columnIndex,
        top: startMinutes * heightPerMinute,
        height: duration * heightPerMinute,
        isInSelectedWindow,
      }
    })
  }, [
    roomColumns,
    startTime,
    selectedTimeStart,
    selectedTimeEnd,
    timelineSessions,
    heightPerMinute,
  ])

  // Update selected sessions when selection area changes
  useEffect(() => {
    // Cancel any pending session selection RAF
    if (sessionSelectionRafRef.current) {
      cancelAnimationFrame(sessionSelectionRafRef.current)
    }

    // Use RAF to debounce session selection updates
    const rafId = requestAnimationFrame(() => {
      sessionSelectionRafRef.current = null

      const sessionsInWindow = allSessions.filter(({ startTime, endTime }) => {
        // Two time ranges overlap if:
        // - Selection starts before session ends AND
        // - Session starts before selection ends
        return (
          selectedTimeStart.isBefore(endTime) &&
          startTime.isBefore(selectedTimeEnd)
        )
      })

      setSelectedSessionIds(sessionsInWindow.map((session) => session.id))
    })

    sessionSelectionRafRef.current = rafId
  }, [selectedTimeStart, selectedTimeEnd, allSessions, setSelectedSessionIds])

  // Auto-position selection window around the session closest to current time when data loads
  useEffect(() => {
    if (isSelectionFocusedOnCurrentTime) {
      return
    }

    if (timelineSessions.length > 0) {
      let referenceTime = getNow()

      // Clamp current time to timeline bounds if it's outside
      if (referenceTime < startTime) {
        referenceTime = startTime
      } else if (referenceTime > endTime) {
        referenceTime = endTime
      }

      // Find the session closest to reference time
      let closestSession = timelineSessions[0]
      let smallestTimeDiff = Math.abs(
        referenceTime.diff(timelineSessions[0].eventStart, "minutes")
      )

      timelineSessions.forEach((session) => {
        // Check distance to session start
        const startDiff = Math.abs(
          referenceTime.diff(session.eventStart, "minutes")
        )
        // Check distance to session end
        const endDiff = Math.abs(
          referenceTime.diff(session.eventEnd, "minutes")
        )
        // Use the smaller of the two distances
        const sessionDiff = Math.min(startDiff, endDiff)

        if (sessionDiff < smallestTimeDiff) {
          smallestTimeDiff = sessionDiff
          closestSession = session
        }
      })

      // Position selection window to start 30 minutes before the closest session
      const selectionStart = closestSession.eventStart.subtract(30, "minutes")

      // Calculate the selection end time
      const selectionEnd = selectionStart.add(selectionDurationHours, "hours")

      // Ensure both start and end are within timeline bounds
      let finalSelectionStart = selectionStart

      if (finalSelectionStart < startTime) {
        finalSelectionStart = startTime
      } else if (selectionEnd > endTime) {
        // If selection would extend past end, move start back
        finalSelectionStart = endTime.subtract(selectionDurationHours, "hours")
        // Double-check it doesn't go before start
        if (finalSelectionStart < startTime) {
          finalSelectionStart = startTime
        }
      }

      setSelectedTimeStart(finalSelectionStart)
      setIsSelectionFocusedOnCurrentTime(true)
    }
  }, [
    timelineSessions,
    startTime,
    endTime,
    selectionDurationHours,
    isSelectionFocusedOnCurrentTime,
  ])

  // Helper function for consistent scroll positioning
  const scrollToSelectionTop = useCallback(() => {
    if (timelineRef.current) {
      const viewportHeight = timelineRef.current.clientHeight
      const targetPosition = viewportHeight * 0.2 // 20% from top
      const scrollTop = Math.max(0, selectionBoxPosition.top - targetPosition)

      timelineRef.current.scrollTo({
        top: scrollTop,
        behavior: "smooth",
      })
    }
  }, [selectionBoxPosition.top])

  // Effect to handle scrolling after session click
  useEffect(() => {
    if (shouldScrollAfterSessionClick) {
      const timeoutId = setTimeout(() => {
        scrollToSelectionTop()
        setShouldScrollAfterSessionClick(false)
      }, 100)

      return () => clearTimeout(timeoutId)
    }
  }, [shouldScrollAfterSessionClick, scrollToSelectionTop])

  // Auto-scroll function for drag operations
  const handleAutoScroll = useCallback(
    (clientY: number) => {
      if (!timelineRef.current) return
      if (isDragging || isResizing) return

      const rect = timelineRef.current.getBoundingClientRect()
      const scrollThreshold = 50 // pixels from edge to trigger scroll
      const scrollSpeed = 8 // pixels per scroll step

      const distanceFromTop = clientY - rect.top
      const distanceFromBottom = rect.bottom - clientY

      let shouldScroll = false
      let scrollDirection = 0

      // Trigger scroll when near top edge OR above the viewport
      if (distanceFromTop < scrollThreshold) {
        // Check if we can actually scroll up (not at top of timeline)
        const currentScrollTop = timelineRef.current.scrollTop
        if (currentScrollTop > 0) {
          scrollDirection = -scrollSpeed
          shouldScroll = true
        }
      } else if (distanceFromBottom < scrollThreshold) {
        // Check if we can actually scroll down (not at bottom of timeline)
        const currentScrollTop = timelineRef.current.scrollTop
        const maxScrollTop =
          timelineRef.current.scrollHeight - timelineRef.current.clientHeight
        if (currentScrollTop < maxScrollTop) {
          scrollDirection = scrollSpeed
          shouldScroll = true
        }
      }

      if (shouldScroll) {
        if (!autoScrollInterval) {
          const interval = setInterval(() => {
            if (timelineRef.current) {
              const oldScrollTop = timelineRef.current.scrollTop
              timelineRef.current.scrollTop += scrollDirection

              // Update dragStartScrollTop to maintain consistent reference
              const actualScrollDelta =
                timelineRef.current.scrollTop - oldScrollTop
              if (actualScrollDelta !== 0) {
                setDragStartScrollTop((prev) => prev + actualScrollDelta)
              } else {
                // If we couldn't scroll (hit bounds), clear the interval
                clearInterval(interval)
                setAutoScrollInterval(null)
              }
            }
          }, 16) // ~60fps
          setAutoScrollInterval(interval)
        }
      } else {
        // Clear auto-scroll when not near edges
        if (autoScrollInterval) {
          clearInterval(autoScrollInterval)
          setAutoScrollInterval(null)
        }
      }
    },
    [autoScrollInterval, isDragging, isResizing]
  )

  const scrollToSession = (sessionId: string) => {
    const session = positionedSessions.find((s) => s.id === sessionId)
    if (session && timelineRef.current) {
      // Align selection window to the start of the clicked session
      const sessionStart = session.eventStart
      const newSelectionStart = sessionStart

      // Ensure the selection doesn't go before the timeline start
      if (newSelectionStart < startTime) {
        setSelectedTimeStart(startTime)
      } else {
        // Also ensure the selection doesn't extend beyond the timeline end
        const maxStartTime = endTime.subtract(selectionDurationHours, "hours")

        if (newSelectionStart > maxStartTime) {
          setSelectedTimeStart(maxStartTime)
        } else {
          setSelectedTimeStart(newSelectionStart)
        }
      }

      // Trigger scroll after state updates
      setShouldScrollAfterSessionClick(true)
    }
  }

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      // Cancel any pending RAF to avoid stale updates
      if (selectionUpdateRafRef.current) {
        cancelAnimationFrame(selectionUpdateRafRef.current)
      }

      // Use RAF to debounce selection updates
      const rafId = requestAnimationFrame(() => {
        selectionUpdateRafRef.current = null

        if (isDragging && dragStartTime) {
          // Use current scroll position directly instead of calculating deltas
          const currentScrollTop = timelineRef.current?.scrollTop || 0

          // Calculate the mouse position relative to the timeline content
          const rect = timelineRef.current?.getBoundingClientRect()
          if (!rect) return

          const mouseYInTimeline = e.clientY - rect.top + currentScrollTop
          const dragStartYInTimeline =
            dragStartY - rect.top + dragStartScrollTop

          const deltaY = mouseYInTimeline - dragStartYInTimeline
          const deltaMinutes = deltaY / heightPerMinute

          const newStartTime = dragStartTime.add(deltaMinutes, "minutes")

          // Constrain to timeline bounds
          const maxStartTime = endTime.subtract(selectionDurationHours, "hours")

          if (newStartTime < startTime) {
            setSelectedTimeStart(startTime)
          } else if (newStartTime > maxStartTime) {
            setSelectedTimeStart(maxStartTime)
          } else {
            setSelectedTimeStart(newStartTime)
          }
        } else if (isResizing && dragStartTime) {
          // Use current scroll position directly for resize operations
          const currentScrollTop = timelineRef.current?.scrollTop || 0

          const rect = timelineRef.current?.getBoundingClientRect()
          if (!rect) return

          const mouseYInTimeline = e.clientY - rect.top + currentScrollTop
          const dragStartYInTimeline =
            dragStartY - rect.top + dragStartScrollTop

          const deltaY = mouseYInTimeline - dragStartYInTimeline
          const deltaMinutes = deltaY / heightPerMinute

          if (isResizing === "top") {
            // Resize from top - adjust start time while keeping end time fixed
            const newStartTime = dragStartTime.add(deltaMinutes, "minutes")

            // Calculate original end time using milliseconds for precision
            const originalEndTime = dragStartTime.add(
              dragStartDuration,
              "hours"
            )

            const newDuration = originalEndTime.diff(
              newStartTime,
              "hours",
              true
            )

            // Ensure minimum duration and valid bounds
            if (
              newDuration >= 0.5 &&
              newStartTime >= startTime &&
              newStartTime < originalEndTime
            ) {
              setSelectedTimeStart(newStartTime)
              setSelectionDurationHours(newDuration)
            }
          } else if (isResizing === "bottom") {
            // Resize from bottom - adjust duration only
            const deltaHours = deltaMinutes / 60
            const newDuration = Math.max(0.5, dragStartDuration + deltaHours)
            const maxDuration = endTime.diff(selectedTimeStart, "hours", true)

            setSelectionDurationHours(Math.min(newDuration, maxDuration))
          }
        }
      })

      selectionUpdateRafRef.current = rafId
    },
    [
      isDragging,
      isResizing,
      dragStartTime,
      dragStartY,
      dragStartScrollTop,
      startTime,
      endTime,
      selectionDurationHours,
      dragStartDuration,
      heightPerMinute,
      selectedTimeStart,
    ]
  )

  const handleMouseUp = useCallback(() => {
    if (isDragging || isResizing) {
      setIsDragging(false)
      setIsResizing(null)
      setDragStartY(0)
      setDragStartTime(null)
      setDragStartDuration(0)
      setDragStartScrollTop(0)

      // Clear auto-scroll interval
      if (autoScrollInterval) {
        clearInterval(autoScrollInterval)
        setAutoScrollInterval(null)
      }

      // Clear any pending selection update RAF
      if (selectionUpdateRafRef.current) {
        cancelAnimationFrame(selectionUpdateRafRef.current)
        selectionUpdateRafRef.current = null
      }

      // Clear any pending session selection RAF
      if (sessionSelectionRafRef.current) {
        cancelAnimationFrame(sessionSelectionRafRef.current)
        sessionSelectionRafRef.current = null
      }

      // Clear any existing prevent scroll timeout
      if (preventScrollTimeout) {
        clearTimeout(preventScrollTimeout)
      }

      // Show selection info tooltip
      setShowSelectionTooltip(true)
      setTimeout(() => setShowSelectionTooltip(false), 3000) // Hide after 3 seconds

      // Reset scroll prevention without forcing scroll position
      const timeoutId = setTimeout(() => {
        // setShouldPreventScroll(false)
        setPreventScrollTimeout(null)
      }, 100)
      setPreventScrollTimeout(timeoutId)
    } else {
      // setShouldPreventScroll(false)
    }
  }, [isDragging, isResizing, autoScrollInterval, preventScrollTimeout])

  // Add global mouse event listeners when dragging/resizing
  useEffect(() => {
    if (isDragging || isResizing) {
      let rafId: number | null = null
      let lastMouseEvent: MouseEvent | null = null

      const handleGlobalMouseMove = (e: MouseEvent) => {
        lastMouseEvent = e

        // Handle auto-scrolling when dragging near viewport edges
        // if (isDragging || isResizing) {
        //   handleAutoScroll(e.clientY)
        // }

        // Cancel any existing RAF to avoid duplicate calls
        if (rafId !== null) {
          cancelAnimationFrame(rafId)
        }

        if (lastMouseEvent !== null) {
          rafId = requestAnimationFrame(() => {
            // Convert native MouseEvent to React.MouseEvent-like object
            const syntheticEvent = {
              clientY: lastMouseEvent?.clientY,
              preventDefault: () => lastMouseEvent?.preventDefault(),
            } as React.MouseEvent
            handleMouseMove(syntheticEvent)
            rafId = null
          })
        }
      }

      const handleGlobalMouseUp = () => {
        if (rafId) {
          cancelAnimationFrame(rafId)
          rafId = null
        }
        handleMouseUp()
      }

      document.addEventListener("mousemove", handleGlobalMouseMove)
      document.addEventListener("mouseup", handleGlobalMouseUp)

      return () => {
        if (rafId) {
          cancelAnimationFrame(rafId)
        }
        document.removeEventListener("mousemove", handleGlobalMouseMove)
        document.removeEventListener("mouseup", handleGlobalMouseUp)
      }
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp, handleAutoScroll])

  // Cleanup effect to ensure all timeouts/intervals are cleared
  useEffect(() => {
    return () => {
      if (autoScrollInterval) {
        clearInterval(autoScrollInterval)
      }
      if (preventScrollTimeout) {
        clearTimeout(preventScrollTimeout)
      }
      if (selectionUpdateRafRef.current) {
        cancelAnimationFrame(selectionUpdateRafRef.current)
      }
      if (sessionSelectionRafRef.current) {
        cancelAnimationFrame(sessionSelectionRafRef.current)
      }
    }
  }, [autoScrollInterval, preventScrollTimeout])

  // Effect to immediately clear auto-scroll when drag/resize ends
  useEffect(() => {
    if (!isDragging && !isResizing && autoScrollInterval) {
      clearInterval(autoScrollInterval)
      setAutoScrollInterval(null)
    }
  }, [isDragging, isResizing, autoScrollInterval])

  const handleTimelineMouseDown = useCallback(() => {
    if (showSelectionTooltip) {
      setShowSelectionTooltip(false)
    }
  }, [showSelectionTooltip])

  const handleSelectionMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      // setShouldPreventScroll(true)
      setIsDragging(true)
      setDragStartY(e.clientY)
      setDragStartTime(selectedTimeStart)
      setDragStartScrollTop(timelineRef.current?.scrollTop || 0)
    },
    [selectedTimeStart]
  )

  const handleResizeMouseDown = useCallback(
    (edge: "top" | "bottom", e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      // setShouldPreventScroll(true)
      setIsResizing(edge)
      setDragStartY(e.clientY)
      setDragStartTime(selectedTimeStart)
      setDragStartDuration(selectionDurationHours)
      setDragStartScrollTop(timelineRef.current?.scrollTop || 0)
    },
    [selectedTimeStart, selectionDurationHours]
  )

  // Show loading state
  if (!isReady) {
    return (
      <Flex col gap={10} style={{ marginTop: 30, paddingLeft: PADDING_LEFT }}>
        <div
          style={{
            height: "calc(100vh - 90px)",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin size="large" />
        </div>
      </Flex>
    )
  }

  // EMPTY STATE shows 8am to 6pm - Guard against both undefined and empty arrays
  if (isReady && !allSessions?.length) {
    const emptyStartTime = getNow().hour(START_TIME)
    const emptyEndTime = getNow().hour(END_TIME)

    const emptyTotalMinutes = emptyEndTime.diff(emptyStartTime, "minutes")
    const minTimelineMinutes = MIN_TIMELINE_HOURS * 60

    // Ensure minimum timeline span for empty state too
    const finalEmptyTotalMinutes = Math.max(
      emptyTotalMinutes,
      minTimelineMinutes
    )
    const emptyHeightPerMinute = containerHeight / finalEmptyTotalMinutes

    // Generate hour markers for empty state
    const emptyHourMarkers = []
    let current = emptyStartTime
    let bailCounter = 0

    while (current <= emptyEndTime) {
      // This is a safety check to prevent infinite loops
      bailCounter++
      if (bailCounter > 10) {
        console.log("Bailed out of empty hour markers")
        break
      }
      const minutesFromStart = current.diff(emptyStartTime, "minutes")
      emptyHourMarkers.push({
        time: formatTimeLocale(current),
        position: minutesFromStart * emptyHeightPerMinute,
      })
      current = current.add(1, "hour")
    }

    return (
      <Flex col gap={10} style={{ marginTop: 30, paddingLeft: PADDING_LEFT }}>
        <div
          style={{
            position: "relative",
            overflowY: "auto",
            overflowX: "hidden",
            height: "calc(100vh - 100px)",
            width: "100%",
            maxWidth: "160px",
            display: "flex",
          }}
        >
          <motion.div
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            style={{ display: "flex" }}
          >
            <Flex col style={{ position: "relative" }}>
              {emptyHourMarkers.map((marker) => (
                <TimeLabel
                  time={marker.time}
                  marker={marker}
                  key={marker.time}
                />
              ))}
            </Flex>

            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 180,
                height: finalEmptyTotalMinutes * emptyHeightPerMinute,
              }}
            >
              <div
                style={{
                  textAlign: "center",
                  color: theme.black,
                  fontSize: 16,
                  fontWeight: 500,
                  transform: "rotate(-90deg)",
                  backgroundColor: theme.highlight + "dd",
                  borderRadius: 10,
                  padding: 10,
                }}
              >
                No class sessions scheduled
              </div>
            </div>
          </motion.div>
        </div>
      </Flex>
    )
  }

  // Normal timeline
  return (
    <Flex
      col
      gap={10}
      style={{
        marginTop: 30,
        width: 200,
        maxWidth: 200,
        overflow: "hidden",
        paddingLeft: PADDING_LEFT,
      }}
    >
      {/* Timeline */}
      <div
        ref={timelineRef}
        style={{
          position: "relative",
          overflowY: "auto",
          height: "calc(100vh - 90px)",
          width: "100%",
          display: "flex",
        }}
        onMouseDown={handleTimelineMouseDown}
      >
        {/* Time labels column */}
        {timelineSessions.length > 0 && (
          <motion.div
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            style={{ display: "flex" }}
          >
            <Flex col style={{ position: "relative" }}>
              {hourMarkers.map((marker) => (
                <TimeLabel
                  time={marker.time}
                  marker={marker}
                  key={marker.time}
                />
              ))}
            </Flex>

            {/* Session columns */}
            <div
              style={{
                position: "relative",
                display: "flex",
                marginLeft: 50,
              }}
            >
              {/* Selection Box Overlay */}
              <Tooltip
                title={
                  <div
                    style={{
                      fontSize: "10px",
                      padding: "4px 6px",
                      minHeight: "auto",
                    }}
                  >
                    <div style={{ fontWeight: "bold" }}>
                      Selected Window: {formatTimeLocale(selectedTimeStart)} -{" "}
                      {formatTimeLocale(selectedTimeEnd)}
                    </div>
                    <div>
                      Duration: {selectionDurationHours.toFixed(1)} hours
                    </div>
                    <div>
                      Sessions:{" "}
                      {
                        positionedSessions.filter((s) => s.isInSelectedWindow)
                          .length
                      }
                    </div>
                  </div>
                }
                open={showSelectionTooltip}
                placement="topRight"
                arrow
              >
                <DraggableSelectionArea
                  top={selectionBoxPosition.top}
                  height={selectionBoxPosition.height}
                  isDragging={isDragging}
                  isResizing={isResizing}
                  onDragStart={handleSelectionMouseDown}
                  onResizeStart={handleResizeMouseDown}
                />
              </Tooltip>

              {roomColumns.map(({ room, color }, columnIndex) => {
                return (
                  <div
                    key={room}
                    style={{
                      position: "relative",
                      width: 10,
                      height: totalMinutes * heightPerMinute,
                    }}
                  >
                    {/* Sessions in this column */}
                    {positionedSessions
                      .filter((session) => session.columnIndex === columnIndex)
                      .map((session) => (
                        <SessionStripe
                          key={session.id}
                          session={session}
                          color={color}
                          left={0}
                          width={10}
                          top={session.top}
                          height={session.height}
                          onClick={() => scrollToSession(session.id)}
                        />
                      ))}
                  </div>
                )
              })}

              {/* Current time marker */}
              {currentTimePosition !== null && (
                <CurrentTimeMarker
                  currentTimePosition={currentTimePosition}
                  isOverflow={
                    currentTimePosition < 0 ||
                    currentTimePosition > totalMinutes * heightPerMinute
                  }
                />
              )}
            </div>
          </motion.div>
        )}
      </div>
    </Flex>
  )
}

export default React.memo(ClassScheduleTimeline)
